import * as React from "react";
import {Button, Grid, Header, Label, List, Modal, Tab, Table} from "semantic-ui-react";
import {ConversionJobLog} from "../../../../../../generated/api";
import {formatDate} from "../../../../../../format";

export interface FtpExplorerConversionJobLogModalProps {
    isOpen: boolean;
    onClose: () => void;
    isLoading: boolean;
    errorMessage: string | null;
    logs: ConversionJobLog[];
    log: ConversionJobLog | null;
    onSelectLog: (log: ConversionJobLog) => void;
    requestedFilePath: string | null;
}

export function FtpExplorerConversionJobLogModal(props: React.PropsWithChildren<FtpExplorerConversionJobLogModalProps>) {
    const {
        isOpen,
        onClose,
        isLoading,
        errorMessage,
        logs,
        log,
        onSelectLog,
        requestedFilePath,
    } = props;

    function renderDetailsTab(selectedLog: ConversionJobLog) {
        return <>
            <Table celled compact size="small">
                <Table.Body>
                    <Table.Row>
                        <Table.Cell width={4}><b>Input file</b></Table.Cell>
                        <Table.Cell>{selectedLog.inputFileName || "-"}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><b>Archive file</b></Table.Cell>
                        <Table.Cell>{selectedLog.archiveFileName || "-"}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><b>Result</b></Table.Cell>
                        <Table.Cell>{selectedLog.result || "-"}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell><b>Created</b></Table.Cell>
                        <Table.Cell>{selectedLog.created ? formatDate(selectedLog.created) : "-"}</Table.Cell>
                    </Table.Row>
                </Table.Body>
            </Table>

            <Header as="h4">Task log</Header>
            <Table celled compact size="small">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Stage</Table.HeaderCell>
                        <Table.HeaderCell>Result</Table.HeaderCell>
                        <Table.HeaderCell>Start</Table.HeaderCell>
                        <Table.HeaderCell>End</Table.HeaderCell>
                        <Table.HeaderCell>Request ID</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {(selectedLog.taskLog || []).length === 0 && <Table.Row>
                        <Table.Cell colSpan="5">No task log entries available.</Table.Cell>
                    </Table.Row>}
                    {(selectedLog.taskLog || []).map((entry, idx) => (
                        <Table.Row key={idx}>
                            <Table.Cell>{entry.stage || "-"}</Table.Cell>
                            <Table.Cell>{entry.result || "-"}</Table.Cell>
                            <Table.Cell>{entry.startTimestamp ? formatDate(entry.startTimestamp) : "-"}</Table.Cell>
                            <Table.Cell>{entry.endTimestamp ? formatDate(entry.endTimestamp) : "-"}</Table.Cell>
                            <Table.Cell>{entry.requestId || "-"}</Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        </>;
    }

    function renderJsonTab(selectedLog: ConversionJobLog) {
        return <pre
            style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "monospace",
                maxHeight: "60vh",
                overflowY: "auto",
            }}
        >
            {JSON.stringify(selectedLog, null, 2)}
        </pre>;
    }

    function parseCreatedTime(entry: ConversionJobLog): number {
        if (!entry.created) {
            return 0;
        }
        return new Date(entry.created).getTime();
    }

    function renderLogSelection() {
        return <List selection divided relaxed>
            {logs.map((entry, idx) => {
                const isSelected = !!log && (log._id
                    ? log._id === entry._id
                    : parseCreatedTime(log) === parseCreatedTime(entry) && log.archiveFileName === entry.archiveFileName);
                return <List.Item
                    key={entry._id || `${entry.archiveFileName || "archive"}-${entry.created || "created"}-${idx}`}
                    onClick={() => onSelectLog(entry)}
                    style={{
                        cursor: "pointer",
                        borderRadius: 4,
                        backgroundColor: isSelected ? "#f0f7ff" : "transparent",
                        border: isSelected ? "1px solid #2185d0" : "1px solid transparent",
                        padding: "8px 10px",
                        marginBottom: 4
                    }}
                >
                    <List.Content>
                        <List.Header>{entry.created ? formatDate(entry.created) : "No date"}</List.Header>
                        <List.Description>
                            <b>{entry.result || "-"}</b>
                            {" · "}
                            {entry.archiveFileName || entry.inputFileName || "-"}
                        </List.Description>
                    </List.Content>
                </List.Item>;
            })}
        </List>;
    }

    function renderResult() {
        if (isLoading) {
            return <p>Loading conversion job log...</p>;
        }

        if (errorMessage) {
            return <Label color="red">{errorMessage}</Label>;
        }

        if (!log) {
            return <Label>No conversion job log found.</Label>;
        }

        const panes = [
            {
                menuItem: "Details",
                render: () => <Tab.Pane attached={false}>{renderDetailsTab(log)}</Tab.Pane>
            },
            {
                menuItem: "JSON",
                render: () => <Tab.Pane attached={false}>{renderJsonTab(log)}</Tab.Pane>
            }
        ];

        if (logs.length <= 1) {
            return <Tab menu={{secondary: true, pointing: true}} panes={panes}/>;
        }

        return <Grid stackable columns={2}>
            <Grid.Column width={5}>
                <Header as="h4" style={{marginTop: 0}}>Available job logs</Header>
                {renderLogSelection()}
            </Grid.Column>
            <Grid.Column width={11}>
                <Tab menu={{secondary: true, pointing: true}} panes={panes}/>
            </Grid.Column>
        </Grid>;
    }

    return <Modal
        open={isOpen}
        onClose={onClose}
        closeIcon
        dimmer="blurring"
        size="large"
    >
        <Header icon='info circle' content='Conversion Job Log'/>
        <Modal.Content scrolling>
            {requestedFilePath && <p><b>Requested file:</b> {requestedFilePath}</p>}
            {renderResult()}
            <div style={{marginTop: 12}}>
                <Button onClick={onClose}>close</Button>
            </div>
        </Modal.Content>
    </Modal>;
}
